import { Controller } from "@hotwired/stimulus"

// Controller for ScreenshotGalleryComponent
// Handles layout switching, lightbox, and screenshot comparison
export default class extends Controller {
  static targets = [
    "gridView",
    "timelineView",
    "comparisonView",
    "lightbox",
    "lightboxImage",
    "lightboxTitle",
    "lightboxCaption",
    "downloadLink"
  ]

  static values = {
    comparison: Boolean,
    currentIndex: { type: Number, default: 0 }
  }

  connect() {
    // Get screenshots data from the DOM
    this.screenshots = this.getScreenshotsData()

    // Keyboard navigation
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundKeyHandler)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundKeyHandler)
  }

  getScreenshotsData() {
    const items = this.element.querySelectorAll('.screenshot-item, .screenshot-timeline-item')
    return Array.from(items).map((item, index) => {
      const img = item.querySelector('img')
      const label = item.querySelector('.text-xs.font-medium, .text-sm.font-medium')
      const timestamp = item.querySelector('.text-xs.text-base-content\\/50, .text-xs.text-base-content\\/40')

      return {
        src: img?.src || '',
        label: label?.textContent?.trim() || `Screenshot ${index + 1}`,
        timestamp: timestamp?.textContent?.trim() || '',
        index
      }
    })
  }

  setLayout(event) {
    const layout = event.currentTarget.dataset.layout

    // Update button states
    const buttons = this.element.querySelectorAll('.btn-group .btn')
    buttons.forEach(btn => {
      btn.classList.toggle('btn-active', btn.dataset.layout === layout)
    })

    // Toggle views
    if (this.hasGridViewTarget) {
      this.gridViewTarget.classList.toggle('hidden', layout !== 'grid')
    }
    if (this.hasTimelineViewTarget) {
      this.timelineViewTarget.classList.toggle('hidden', layout !== 'timeline')
    }
    if (this.hasComparisonViewTarget) {
      this.comparisonViewTarget.classList.add('hidden')
    }
  }

  toggleComparison() {
    if (!this.hasComparisonViewTarget) return

    const isHidden = this.comparisonViewTarget.classList.contains('hidden')

    // Hide other views
    if (this.hasGridViewTarget) {
      this.gridViewTarget.classList.toggle('hidden', isHidden)
    }
    if (this.hasTimelineViewTarget) {
      this.timelineViewTarget.classList.toggle('hidden', isHidden)
    }

    // Toggle comparison view
    this.comparisonViewTarget.classList.toggle('hidden', !isHidden)
  }

  openLightbox(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.showImage(index)

    if (this.hasLightboxTarget) {
      this.lightboxTarget.showModal()
    }
  }

  closeLightbox() {
    if (this.hasLightboxTarget) {
      this.lightboxTarget.close()
    }
  }

  showImage(index) {
    if (!this.screenshots || !this.screenshots[index]) return

    const screenshot = this.screenshots[index]
    this.currentIndexValue = index

    if (this.hasLightboxImageTarget) {
      this.lightboxImageTarget.src = screenshot.src
      this.lightboxImageTarget.alt = screenshot.label
    }

    if (this.hasLightboxTitleTarget) {
      this.lightboxTitleTarget.textContent = screenshot.label
    }

    if (this.hasLightboxCaptionTarget) {
      this.lightboxCaptionTarget.textContent = screenshot.timestamp
    }

    if (this.hasDownloadLinkTarget) {
      this.downloadLinkTarget.href = screenshot.src
      this.downloadLinkTarget.download = `screenshot_${index + 1}.png`
    }

    // Update thumbnail selection
    this.updateThumbnailSelection(index)
  }

  updateThumbnailSelection(activeIndex) {
    const thumbnails = this.element.querySelectorAll('[data-action="click->screenshot-gallery#selectImage"]')
    thumbnails.forEach((thumb, index) => {
      thumb.classList.toggle('border-primary', index === activeIndex)
      thumb.classList.toggle('border-transparent', index !== activeIndex)
    })
  }

  selectImage(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.showImage(index)
  }

  prevImage() {
    const newIndex = this.currentIndexValue > 0
      ? this.currentIndexValue - 1
      : this.screenshots.length - 1
    this.showImage(newIndex)
  }

  nextImage() {
    const newIndex = this.currentIndexValue < this.screenshots.length - 1
      ? this.currentIndexValue + 1
      : 0
    this.showImage(newIndex)
  }

  handleKeydown(event) {
    // Only handle when lightbox is open
    if (!this.hasLightboxTarget || !this.lightboxTarget.open) return

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        this.prevImage()
        break
      case 'ArrowRight':
        event.preventDefault()
        this.nextImage()
        break
      case 'Escape':
        event.preventDefault()
        this.closeLightbox()
        break
    }
  }
}
